import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api, STATUS_TO_PILL } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Clock, TrendingUp, AlertTriangle, Package, DollarSign, Filter, RefreshCw } from "lucide-react";

const fetcher = (u) => api.get(u).then(r => r.data);

const STAGES = ["CONFIRMED","PLANNED","IN_PRODUCTION","QC","READY_TO_DISPATCH","PARTIAL_DISPATCH","DISPATCHED","DELIVERED"];
const STAGE_LABEL = {
  ENQUIRY:"Enquiry", QUOTED:"Quoted", CONFIRMED:"Confirmed", PLANNED:"Planned",
  IN_PRODUCTION:"In Production", QC:"In QC", READY_TO_DISPATCH:"Ready to Ship",
  PARTIAL_DISPATCH:"Partial Ship", DISPATCHED:"Dispatched", DELIVERED:"Delivered",
  CANCELLED:"Cancelled", ON_HOLD:"On Hold",
};
const DEPT_COLOR = {
  SALES:"bg-sky-100 text-sky-700 border-sky-200",
  PLANNING:"bg-indigo-100 text-indigo-700 border-indigo-200",
  PRODUCTION:"bg-amber-100 text-amber-700 border-amber-200",
  QC:"bg-purple-100 text-purple-700 border-purple-200",
  STORES:"bg-emerald-100 text-emerald-700 border-emerald-200",
  DISPATCH:"bg-green-100 text-green-700 border-green-200",
};
const STAGE_TO_PILL = {
  ENQUIRY:"pill-idle", QUOTED:"pill-planned", CONFIRMED:"pill-released",
  PLANNED:"pill-planned", IN_PRODUCTION:"pill-running", QC:"pill-hold",
  READY_TO_DISPATCH:"pill-completed", PARTIAL_DISPATCH:"pill-setup",
  DISPATCHED:"pill-approved", DELIVERED:"pill-approved",
  CANCELLED:"pill-rejected", ON_HOLD:"pill-paused",
};

export function OrderTrackerPage() {
  const { data, isLoading, mutate: reload } = useSWR("/order-tracker", fetcher, { refreshInterval: 20000 });
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [customer, setCustomer] = useState("");
  const [dept, setDept] = useState("");
  const [view, setView] = useState("kanban"); // kanban | table
  const navigate = useNavigate();

  const rows = (data||[]).filter(o => {
    if (q && !`${o.so_no} ${o.customer_po_no} ${o.customer_name} ${o.product_name}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (customer && o.customer_id !== customer) return false;
    if (dept && o.department !== dept) return false;
    return true;
  });

  const grouped = STAGES.reduce((acc, s) => ({...acc, [s]: rows.filter(r=>r.status===s)}), {});

  const kpi = {
    active: rows.length,
    delayed: rows.filter(r=>r.delayed).length,
    ready: rows.filter(r=>r.status==="READY_TO_DISPATCH").length,
    value: rows.reduce((s,r)=>s+(r.order_value||0),0),
  };

  return (
    <div className="space-y-4" data-testid="order-tracker-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Order Tracker</h1>
          <p className="text-sm text-slate-500">Live cross-department status. No more phone calls — every order's stage in one view.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={()=>reload()} data-testid="btn-refresh-tracker" className="text-xs px-3 py-1.5 border border-slate-200 rounded-md hover:border-sky-400 flex items-center gap-1"><RefreshCw size={12}/>Refresh</button>
          <div className="bg-white border border-slate-200 rounded-md p-0.5 flex text-xs">
            <button data-testid="btn-view-kanban" onClick={()=>setView("kanban")} className={`px-3 py-1 rounded ${view==="kanban"?"bg-sky-600 text-white":"text-slate-600"}`}>Kanban</button>
            <button data-testid="btn-view-table" onClick={()=>setView("table")} className={`px-3 py-1 rounded ${view==="table"?"bg-sky-600 text-white":"text-slate-600"}`}>Table</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiSmall testid="ot-kpi-active" label="Active Orders" value={kpi.active} icon={Package} tone=""/>
        <KpiSmall testid="ot-kpi-ready" label="Ready to Dispatch" value={kpi.ready} icon={TrendingUp} tone="status-good"/>
        <KpiSmall testid="ot-kpi-delayed" label="Delayed" value={kpi.delayed} icon={AlertTriangle} tone={kpi.delayed>0?"status-danger":""}/>
        <KpiSmall testid="ot-kpi-value" label="Open Order Value" value={`₹${(kpi.value/100000).toFixed(2)}L`} icon={DollarSign}/>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap gap-2 items-center">
        <Filter size={14} className="text-slate-400"/>
        <input data-testid="tracker-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search SO#, PO#, customer, product..." className="text-sm px-3 py-1.5 rounded-md border border-slate-200 flex-1 min-w-[220px]"/>
        <select data-testid="tracker-filter-dept" value={dept} onChange={e=>setDept(e.target.value)} className="text-sm px-2 py-1.5 rounded-md border border-slate-200">
          <option value="">All Departments</option>
          {["SALES","PLANNING","PRODUCTION","QC","STORES","DISPATCH"].map(d=><option key={d}>{d}</option>)}
        </select>
      </div>

      {isLoading && <div className="text-slate-500 text-sm">Loading orders...</div>}

      {view === "kanban" ? (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3 min-w-max">
            {STAGES.map(st => (
              <div key={st} className="w-72 shrink-0" data-testid={`kanban-col-${st}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{STAGE_LABEL[st]}</div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 font-mono">{grouped[st].length}</span>
                </div>
                <div className="space-y-2">
                  {grouped[st].map(o => (
                    <OrderCard key={o.so_id} order={o} onClick={()=>setSelected(o.so_id)}/>
                  ))}
                  {grouped[st].length===0 && <div className="text-xs text-slate-400 italic py-4 text-center border border-dashed border-slate-200 rounded-lg">No orders</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-auto">
          <table className="data-table" data-testid="tracker-table">
            <thead><tr><th>SO No</th><th>Customer PO</th><th>Customer</th><th>Product</th><th>Ordered</th><th>Produced</th><th>Dispatched</th><th>Balance</th><th>Progress</th><th>Due</th><th>Stage</th><th>Dept</th></tr></thead>
            <tbody>
              {rows.map(o => (
                <tr key={o.so_id} onClick={()=>setSelected(o.so_id)} className="cursor-pointer" data-testid={`tracker-row-${o.so_no}`}>
                  <td className="font-mono text-xs">{o.so_no}</td>
                  <td className="font-mono text-xs">{o.customer_po_no}</td>
                  <td>{o.customer_name}</td>
                  <td>{o.product_name}</td>
                  <td className="font-mono">{o.ordered_qty?.toLocaleString()}</td>
                  <td className="font-mono">{(o.produced_qty||0).toLocaleString()}</td>
                  <td className="font-mono">{(o.dispatched_qty||0).toLocaleString()}</td>
                  <td className="font-mono">{(o.balance_qty||0).toLocaleString()}</td>
                  <td><div className="progress-bar w-24"><span style={{width:`${Math.min(100, o.progress_pct||0)}%`}}/></div><span className="text-[10px] text-slate-500">{o.progress_pct||0}%</span></td>
                  <td className="text-xs"><span className={o.delayed?"text-red-600 font-semibold":""}>{o.due_date?.slice(0,10)}{o.days_left!==null && ` (${o.days_left}d)`}</span></td>
                  <td><span className={`pill ${STAGE_TO_PILL[o.status]}`}>{STAGE_LABEL[o.status]}</span></td>
                  <td><span className={`text-[10px] px-2 py-0.5 rounded-full border ${DEPT_COLOR[o.department]}`}>{o.department}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && <OrderDetailDrawer so_id={selected} onClose={()=>setSelected(null)}/>}
    </div>
  );
}

const KpiSmall = ({label, value, icon:Icon, tone, testid}) => (
  <div className={`kpi-card ${tone}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <div>
        <div className="kpi-label">{label}</div>
        <div className="kpi-value mt-1">{value}</div>
      </div>
      {Icon && <Icon size={18} className="text-slate-400"/>}
    </div>
  </div>
);

const OrderCard = ({order, onClick}) => (
  <div onClick={onClick} data-testid={`order-card-${order.so_no}`}
    className={`bg-white border rounded-lg p-3 cursor-pointer hover:border-sky-400 hover:shadow-sm transition ${order.delayed?"border-red-300":"border-slate-200"}`}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[11px] font-mono text-slate-500">{order.so_no}</span>
      {order.priority==="HIGH" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">HIGH</span>}
    </div>
    <div className="text-sm font-semibold text-slate-900 truncate">{order.customer_name}</div>
    <div className="text-xs text-slate-500 truncate">{order.product_name}</div>
    <div className="mt-2 flex items-center justify-between text-[11px]">
      <span className="font-mono text-slate-700">{order.ordered_qty?.toLocaleString()} pcs</span>
      <span className={`flex items-center gap-1 ${order.delayed?"text-red-600 font-semibold":"text-slate-500"}`}>
        <Clock size={10}/>{order.days_left!==null ? `${order.days_left}d` : "—"}
      </span>
    </div>
    <div className="progress-bar mt-2"><span style={{width:`${Math.min(100, order.progress_pct||0)}%`}}/></div>
    <div className="mt-1 text-[10px] text-slate-500 flex items-center justify-between">
      <span>{order.progress_pct||0}% done</span>
      <span className={`px-1.5 py-0.5 rounded-full border ${DEPT_COLOR[order.department]}`}>{order.department}</span>
    </div>
  </div>
);

const OrderDetailDrawer = ({so_id, onClose}) => {
  const { data, isLoading } = useSWR(`/order-tracker/${so_id}`, fetcher, { refreshInterval: 15000 });
  const so = data?.sales_order;
  const events = data?.events || [];

  const advance = async (status) => {
    try { await api.patch(`/sales-orders/${so_id}`, { status }); toast.success(`Order → ${status}`); mutate(`/order-tracker/${so_id}`); mutate("/order-tracker"); }
    catch(e){ toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose}/>
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl" data-testid="order-detail-drawer">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="text-[11px] font-mono text-slate-500">{so?.so_no}</div>
            <h2 className="text-lg font-bold text-slate-900">{data?.customer?.name}</h2>
            <div className="text-xs text-slate-500 mt-0.5">{data?.product?.name} · Cust PO: <span className="font-mono">{so?.customer_po_no||"-"}</span></div>
          </div>
          <button onClick={onClose} data-testid="btn-close-drawer" className="text-slate-400 hover:text-slate-900">✕</button>
        </div>

        {isLoading && <div className="p-6 text-slate-500 text-sm">Loading...</div>}
        {so && (
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Ordered" value={so.ordered_qty?.toLocaleString()}/>
              <Stat label="Produced" value={(so.produced_qty||0).toLocaleString()}/>
              <Stat label="Dispatched" value={(so.dispatched_qty||0).toLocaleString()}/>
              <Stat label="FG Ready" value={(so.fg_ready_qty||0).toLocaleString()}/>
              <Stat label="Balance" value={(so.balance_qty||0).toLocaleString()}/>
              <Stat label="Value" value={`₹${(so.order_value||0).toLocaleString()}`}/>
            </div>

            <StageBar current={so.status}/>

            <div>
              <div className="text-xs font-semibold uppercase text-slate-600 mb-2">Department status</div>
              <div className="space-y-2">
                <DeptRow dept="SALES" so={so} data={data}/>
                <DeptRow dept="PLANNING" so={so} data={data}/>
                <DeptRow dept="PRODUCTION" so={so} data={data}/>
                <DeptRow dept="QC" so={so} data={data}/>
                <DeptRow dept="STORES" so={so} data={data}/>
                <DeptRow dept="DISPATCH" so={so} data={data}/>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              {so.status !== "ON_HOLD" && <button data-testid="btn-so-hold" onClick={()=>advance("ON_HOLD")} className="text-xs px-3 py-1.5 rounded border border-slate-200 hover:border-amber-500">Put On Hold</button>}
              {so.status === "ON_HOLD" && <button data-testid="btn-so-resume" onClick={()=>advance("CONFIRMED")} className="text-xs px-3 py-1.5 rounded bg-sky-600 text-white">Resume</button>}
              {so.status === "DISPATCHED" && <button data-testid="btn-so-delivered" onClick={()=>advance("DELIVERED")} className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white">Mark Delivered</button>}
              {!["CANCELLED","DELIVERED"].includes(so.status) && <button data-testid="btn-so-cancel" onClick={()=>{if(confirm("Cancel order?")) advance("CANCELLED")}} className="text-xs px-3 py-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50">Cancel</button>}
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-slate-600 mb-2">Timeline</div>
              <div className="space-y-2" data-testid="order-timeline">
                {events.length===0 && <div className="text-xs text-slate-400 italic">No events yet</div>}
                {events.map(e => (
                  <div key={e.event_id} className="flex gap-3 text-xs">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border h-fit ${DEPT_COLOR[e.department]}`}>{e.department}</span>
                    <div className="flex-1">
                      <div className="text-slate-800 font-medium">{e.event.replaceAll("_"," ")}</div>
                      <div className="text-slate-500">{e.timestamp?.slice(0,16).replace("T"," ")} · {e.by}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({label, value}) => (
  <div className="bg-slate-50 rounded-lg p-3">
    <div className="text-[10px] uppercase text-slate-500 font-semibold">{label}</div>
    <div className="font-mono text-base font-bold text-slate-900">{value}</div>
  </div>
);

const StageBar = ({current}) => {
  const idx = STAGES.indexOf(current);
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-slate-600 mb-2">Pipeline</div>
      <div className="flex items-center gap-0.5 overflow-x-auto">
        {STAGES.map((s, i) => (
          <div key={s} className="flex items-center">
            <div className={`px-2 py-1 rounded text-[10px] font-semibold ${i<=idx?"bg-sky-600 text-white":"bg-slate-100 text-slate-500"}`}>{STAGE_LABEL[s]}</div>
            {i<STAGES.length-1 && <span className={`w-3 h-0.5 ${i<idx?"bg-sky-600":"bg-slate-200"}`}/>}
          </div>
        ))}
      </div>
    </div>
  );
};

const DeptRow = ({dept, so, data}) => {
  const pos = data?.production_orders || [];
  const qcs = data?.qc_batches || [];
  const fgs = data?.fg || [];
  const disps = data?.dispatches || [];
  let status = "—";
  let detail = "";
  if (dept==="SALES") {
    status = so.status==="CONFIRMED" ? "Confirmed" : (["ENQUIRY","QUOTED"].includes(so.status) ? "Quoting" : "Booked");
    detail = so.customer_po_no ? `PO: ${so.customer_po_no}` : "No customer PO";
  } else if (dept==="PLANNING") {
    if (pos.length===0) { status = "Pending"; detail="No PO created yet"; }
    else { status = "Planned"; detail = pos.map(p=>p.po_no).join(", "); }
  } else if (dept==="PRODUCTION") {
    if (pos.some(p=>p.status==="RUNNING")) { status="Running"; detail=`${so.produced_qty||0} / ${so.planned_qty||0} produced`; }
    else if (pos.some(p=>p.status==="COMPLETED")) { status="Completed"; detail=`${so.produced_qty||0} produced`; }
    else if (pos.length>0) { status="Not started"; detail=`${pos.length} PO(s)`; }
    else { status = "Pending"; detail="Awaiting planning"; }
  } else if (dept==="QC") {
    const p = qcs.filter(q=>q.status==="PENDING").length;
    const a = qcs.filter(q=>q.status==="APPROVED").length;
    if (p) { status="In inspection"; detail=`${p} pending, ${a} approved`; }
    else if (a) { status="Cleared"; detail=`${a} batch(es) approved`; }
    else { status="—"; detail="No QC yet"; }
  } else if (dept==="STORES") {
    if (so.fg_ready_qty>0) { status="Ready"; detail=`${so.fg_ready_qty} pcs in FG store`; }
    else { status="—"; detail="No FG stock yet"; }
  } else if (dept==="DISPATCH") {
    if (disps.length>0) { status="Shipped"; detail=`${disps.length} DN, ${so.dispatched_qty||0} pcs`; }
    else { status="Pending"; detail="Not dispatched"; }
  }
  return (
    <div className="flex items-start gap-3 p-2 rounded-lg bg-slate-50">
      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${DEPT_COLOR[dept]} h-fit`}>{dept}</span>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-slate-800">{status}</div>
        <div className="text-[11px] text-slate-500 truncate">{detail}</div>
      </div>
    </div>
  );
};

// ---------- Sales Orders (create + list) ----------
export function SalesOrdersPage() {
  const { data: sos } = useSWR("/sales-orders", fetcher);
  const { data: customers } = useSWR("/customers", fetcher);
  const { data: products } = useSWR("/products", fetcher);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ priority:"NORMAL", gst_pct:18 });
  const cMap = Object.fromEntries((customers||[]).map(c=>[c.customer_id,c]));
  const pMap = Object.fromEntries((products||[]).map(p=>[p.product_id,p]));

  const submit = async () => {
    try { await api.post("/sales-orders", f); toast.success("Sales order created"); setShow(false); setF({priority:"NORMAL",gst_pct:18}); mutate("/sales-orders"); mutate("/order-tracker"); }
    catch(e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="sales-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sales Orders</h1>
          <p className="text-sm text-slate-500">{(sos||[]).length} orders in the pipeline</p>
        </div>
        <button data-testid="btn-create-sales-order" onClick={()=>setShow(true)} className="bg-sky-600 text-white text-sm px-3 py-1.5 rounded-md">+ New Sales Order</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-auto">
        <table className="data-table" data-testid="sales-orders-table">
          <thead><tr><th>SO No</th><th>Customer PO</th><th>Customer</th><th>Product</th><th>Qty</th><th>Value</th><th>Due</th><th>Priority</th><th>Stage</th></tr></thead>
          <tbody>
            {(sos||[]).map(o=>(
              <tr key={o.so_id} data-testid={`so-row-${o.so_no}`}>
                <td className="font-mono text-xs">{o.so_no}</td>
                <td className="font-mono text-xs">{o.customer_po_no}</td>
                <td>{cMap[o.customer_id]?.name || o.customer_id}</td>
                <td>{pMap[o.product_id]?.name || o.product_id}</td>
                <td className="font-mono">{o.ordered_qty?.toLocaleString()}</td>
                <td className="font-mono">₹{(o.order_value||0).toLocaleString()}</td>
                <td className="text-xs">{o.due_date?.slice(0,10)}</td>
                <td><span className={`text-[10px] px-2 py-0.5 rounded-full ${o.priority==="HIGH"?"bg-red-100 text-red-700":o.priority==="LOW"?"bg-slate-100 text-slate-500":"bg-slate-100 text-slate-700"}`}>{o.priority}</span></td>
                <td><span className={`pill ${STAGE_TO_PILL[o.status]}`}>{STAGE_LABEL[o.status]||o.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl p-5" data-testid="so-modal">
            <h3 className="font-semibold text-slate-900 mb-4">Create Sales Order</h3>
            <div className="grid grid-cols-2 gap-3">
              <SF label="Customer *"><select data-testid="so-input-customer" value={f.customer_id||""} onChange={e=>setF({...f,customer_id:e.target.value})} className="soi"><option value="">Select</option>{(customers||[]).map(c=><option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}</select></SF>
              <SF label="Customer PO No"><input data-testid="so-input-customer-po" value={f.customer_po_no||""} onChange={e=>setF({...f,customer_po_no:e.target.value})} className="soi"/></SF>
              <SF label="Product *"><select data-testid="so-input-product" value={f.product_id||""} onChange={e=>setF({...f,product_id:e.target.value})} className="soi"><option value="">Select</option>{(products||[]).map(p=><option key={p.product_id} value={p.product_id}>{p.name}</option>)}</select></SF>
              <SF label="Ordered Qty *"><input data-testid="so-input-qty" type="number" value={f.ordered_qty||""} onChange={e=>setF({...f,ordered_qty:Number(e.target.value)})} className="soi"/></SF>
              <SF label="Rate (₹/pc)"><input data-testid="so-input-rate" type="number" step="0.01" value={f.rate||""} onChange={e=>setF({...f,rate:Number(e.target.value)})} className="soi"/></SF>
              <SF label="GST %"><input type="number" value={f.gst_pct} onChange={e=>setF({...f,gst_pct:Number(e.target.value)})} className="soi"/></SF>
              <SF label="Due Date"><input data-testid="so-input-due" type="date" value={f.due_date||""} onChange={e=>setF({...f,due_date:e.target.value})} className="soi"/></SF>
              <SF label="Priority"><select value={f.priority} onChange={e=>setF({...f,priority:e.target.value})} className="soi"><option>NORMAL</option><option>HIGH</option><option>LOW</option></select></SF>
              <div className="col-span-2"><SF label="Notes"><textarea value={f.notes||""} onChange={e=>setF({...f,notes:e.target.value})} className="soi" rows={2}/></SF></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={()=>setShow(false)} className="text-sm px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
              <button data-testid="btn-submit-so" onClick={submit} className="text-sm px-3 py-1.5 rounded-md bg-sky-600 text-white">Create</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.soi{width:100%;font-size:.85rem;border:1px solid #E2E8F0;border-radius:6px;padding:.4rem .5rem;background:#fff}`}</style>
    </div>
  );
}

const SF = ({label, children}) => <div><label className="text-[11px] uppercase font-semibold text-slate-500">{label}</label>{children}</div>;
